import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import { getTemplates, getCases, generateDocument, getDirectApiUrl, getSigningStatus, createSigningRequest, generatePdfDocument } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { FileText, Search, ChevronRight, Briefcase, Settings, Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'letters', label: 'Letters' },
  { value: 'fax_cover', label: 'Fax Cover' },
  { value: 'forms', label: 'Forms' },
  { value: 'notices', label: 'Notices' },
  { value: 'disengagements', label: 'Disengagements' },
  { value: 'intake', label: 'Intake' },
  { value: 'demand', label: 'Demand' },
  { value: 'medical', label: 'Medical' },
  { value: 'settlement', label: 'Settlement' },
  { value: 'billing', label: 'Billing' },
  { value: 'misc', label: 'Misc' },
];

export default function LettersFormsPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [catFilter, setCatFilter] = useState('');

  // Case selection modal
  const [showCasePicker, setShowCasePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [cases, setCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [loadingCases, setLoadingCases] = useState(false);

  // Preview state (now handled by new tab)
  const [generating, setGenerating] = useState(false);

  // Signing — post-generation
  const [signingAvailable, setSigningAvailable] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTemplates();
    getSigningStatus().then(s => setSigningAvailable(s?.configured && s?.active)).catch(() => {});
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await getTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  };

  const loadCases = async (q) => {
    setLoadingCases(true);
    try {
      const data = await getCases({ limit: 200 });
      setCases(Array.isArray(data) ? data : (data?.cases || []));
    } catch { setCases([]); }
    finally { setLoadingCases(false); }
  };

  const handleTemplateClick = async (template) => {
    setSelectedTemplate(template);
    setCaseSearch('');
    setShowCasePicker(true);
    await loadCases();
  };

  const handleGenerate = async (caseItem) => {
    if (!selectedTemplate) return;

    // ── PDF template: fill form and save to case documents ──
    if (selectedTemplate.template_type === 'pdf') {
      setGenerating(true);
      try {
        const result = await generatePdfDocument(selectedTemplate._id, { case_id: caseItem._id });
        setGenerating(false);
        setLastGenerated({ result, caseItem, templateName: selectedTemplate.name, isPdf: true });
      } catch (err) {
        toast.error(err.message || 'Failed to generate PDF');
        setGenerating(false);
      }
      return;
    }

    // ── HTML template: existing flow ──
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Pop-up blocked — please allow pop-ups for this site and try again');
      return;
    }
    win.document.write('<html><body style="background:#525659;color:#bbb;font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0"><div style="text-align:center"><div style="width:32px;height:32px;border:3px solid #666;border-top-color:#d4a853;border-radius:50%;animation:sp .7s linear infinite;margin:0 auto 14px"></div><div>Generating document…</div></div><style>@keyframes sp{to{transform:rotate(360deg)}}</style></body></html>');

    setGenerating(true);
    try {
      const result = await generateDocument(selectedTemplate._id, {
        case_id: caseItem._id,
        client_id: caseItem.clients?._id || caseItem.clients,
      });

      // Navigate the already-open tab to the PDF preview
      const token = localStorage.getItem('zeus_token') || '';
      const previewUrl = getDirectApiUrl(`/templates/documents/generated/${result._id}?format=preview&token=${encodeURIComponent(token)}`);
      win.location.href = previewUrl;

      // Show success state inside the modal with signing option
      setGenerating(false);
      setLastGenerated({ result, caseItem, templateName: selectedTemplate.name });
    } catch (err) {
      // Close the blank tab on error
      try { win.close(); } catch {}
      toast.error('Failed to generate document');
      setGenerating(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (catFilter) list = list.filter(t => t.category === catFilter);
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      list = list.filter(t => t.name.toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q));
    }
    return list;
  }, [templates, catFilter, searchQ]);

  const filteredCases = useMemo(() => {
    if (!caseSearch.trim()) return cases;
    const q = caseSearch.toLowerCase();
    return cases.filter(c =>
      (c.client_name || '').toLowerCase().includes(q) ||
      (c.case_number || '').toLowerCase().includes(q) ||
      (c.claim_number || '').toLowerCase().includes(q)
    );
  }, [cases, caseSearch]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <>
      <Header title="Letters & Forms">
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <select className="form-select" style={{ width:'auto', padding:'6px 10px', fontSize:'0.82rem' }}
            value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <div style={{
            display:'flex', alignItems:'center', gap:'6px', padding:'6px 10px',
            background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
            borderRadius:'var(--radius-md)',
          }}>
            <Search size={14} style={{ color:'var(--text-muted)' }} />
            <input style={{
              border:'none', outline:'none', background:'transparent',
              color:'var(--text-primary)', fontSize:'0.85rem', width:'180px',
              fontFamily:'var(--font-body)',
            }} placeholder="Search templates..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
          </div>
          <Link to="/templates" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:'5px' }}>
            <Settings size={14} /> Create New
          </Link>
        </div>
      </Header>

      <div className="app-content" style={{ padding:'20px 24px' }}>
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No templates found</h3>
            <p>Create templates in the Templates section first, then use them here to generate letters and forms.</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'14px' }}>
            {filteredTemplates.map(t => (
              <div
                key={t._id}
                onClick={() => handleTemplateClick(t)}
                className="card"
                style={{
                  padding:'16px', cursor:'pointer', transition:'all 0.15s',
                  borderLeft:'3px solid var(--zeus-gold)',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--zeus-navy-light)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.transform = ''; }}
              >
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'10px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.95rem', color:'var(--text-primary)', marginBottom:'4px' }}>
                      {t.name}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <span className="badge" style={{ fontSize:'0.72rem' }}>
                        {(t.category || 'misc').replace(/_/g, ' ')}
                      </span>
                      {t.template_type === 'pdf' && (
                        <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.65rem', fontWeight: 700, background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
                          PDF Form
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color:'var(--text-muted)', flexShrink:0, marginTop:'2px' }} />
                </div>
                {t.merge_fields?.length > 0 && (
                  <div style={{ marginTop:'8px', fontSize:'0.75rem', color:'var(--text-muted)' }}>
                    {t.merge_fields.length} merge field{t.merge_fields.length !== 1 ? 's' : ''}
                  </div>
                )}
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'4px' }}>
                  Updated {t.updatedAt ? formatDate(t.updatedAt) : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ Case Selection Modal ═══ */}
      <Modal isOpen={showCasePicker} onClose={() => { setShowCasePicker(false); setLastGenerated(null); }} title={lastGenerated ? 'Document Generated' : `Generate: ${selectedTemplate?.name || ''}`}>

        {/* ── Success state after generation ── */}
        {lastGenerated ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <CheckCircle size={24} style={{ color: '#22c55e' }} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
              {lastGenerated.templateName}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 6 }}>
              Generated for {lastGenerated.caseItem?.client_name || 'Client'} -- {lastGenerated.caseItem?.case_number || ''}
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 20 }}>
              {lastGenerated.isPdf
                ? 'The filled PDF has been saved to the case Documents tab.'
                : 'The document is open in a new tab. You can print or save it from there.'
              }
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320, margin: '0 auto' }}>
              {signingAvailable && (
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', gap: 8 }}
                  disabled={submitting}
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      const { result, caseItem, templateName } = lastGenerated;
                      const claimant = caseItem?.clients || {};
                      const recipients = [];
                      if (claimant?.email) recipients.push({ name: claimant.name || `${claimant.first_name || ''} ${claimant.last_name || ''}`.trim(), email: claimant.email, signer_type: 'claimant' });
                      if (recipients.length === 0) recipients.push({ name: '', email: '', signer_type: 'other' });

                      await createSigningRequest({
                        case_id: caseItem._id,
                        title: templateName || result.name || 'Document',
                        recipients,
                        source_document_url: result.url || undefined,
                      });
                      toast.success('DocuSeal request created -- go to the case Documents tab to add recipients and send');
                      setShowCasePicker(false);
                      setLastGenerated(null);
                      navigate(`/cases/${caseItem._id}`, { state: { tab: 'documents' } });
                    } catch (err) {
                      toast.error(err.message || 'Failed to create signing request');
                    } finally { setSubmitting(false); }
                  }}
                >
                  <Send size={14} /> Send via DocuSeal
                </button>
              )}
              <button
                className="btn btn-ghost"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setShowCasePicker(false); setLastGenerated(null); }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (<>
        <p style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:'12px' }}>
          Select a case to fill the template with client and case information:
        </p>

        {/* Search */}
        <div style={{
          display:'flex', alignItems:'center', gap:'8px', padding:'8px 12px',
          background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
          borderRadius:'var(--radius-md)', marginBottom:'12px',
        }}>
          <Search size={14} style={{ color:'var(--text-muted)' }} />
          <input style={{
            border:'none', outline:'none', background:'transparent',
            color:'var(--text-primary)', fontSize:'0.85rem', flex:1,
            fontFamily:'var(--font-body)',
          }} placeholder="Search by claimant, case #, or claim #..." value={caseSearch} onChange={(e) => setCaseSearch(e.target.value)} />
        </div>

        {/* Cases list */}
        <div style={{
          maxHeight:'350px', overflowY:'auto',
          border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)',
        }}>
          {loadingCases ? (
            <div style={{ padding:'20px', textAlign:'center' }}><div className="spinner" /></div>
          ) : filteredCases.length === 0 ? (
            <div style={{ padding:'20px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>
              No cases found
            </div>
          ) : (
            filteredCases.map(c => (
              <div
                key={c._id}
                onClick={() => !generating && handleGenerate(c)}
                style={{
                  display:'flex', alignItems:'center', gap:'12px',
                  padding:'10px 14px', cursor: generating ? 'wait' : 'pointer',
                  borderBottom:'1px solid var(--zeus-border)',
                  transition:'background 0.15s',
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Briefcase size={16} style={{ color:'var(--zeus-gold)', flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.88rem', color:'var(--text-primary)' }}>
                    {c.client_name || 'Unnamed Client'}
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
                    {c.case_number} · {c.claim_number || '—'} · {(c.case_type || '').replace(/_/g, ' ')}
                  </div>
                </div>
                <span className="badge" style={{ fontSize:'0.72rem' }}>
                  {(c.status || 'intake')}
                </span>
              </div>
            ))
          )}
        </div>

        {generating && (
          <div style={{ padding:'12px', textAlign:'center', color:'var(--zeus-gold)', fontSize:'0.85rem' }}>
            <div className="spinner" style={{ marginBottom:'8px' }} /> Generating document...
          </div>
        )}
        </>)}
      </Modal>

    </>
  );
}
